import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import DepsService from 'services/dependencies';
import actionTypes from './action-types';
import * as api from '../resources/dashboard.api';

export const fetchDashboardRequest = () => ({
  type: actionTypes.FETCH_DASHBOARD_REQUEST,
  isLoading: true,
});

export const fetchDashboardSuccess = (current, toSave) => ({
  type: actionTypes.FETCH_DASHBOARD_SUCCESS,
  isLoading: false,
  toSave,
  current,
});

export const fetchDashboardFailure = () => ({
  type: actionTypes.FETCH_DASHBOARD_FAILURE,
  isLoading: false,
});

export const setDashboardLayouts = (layouts) => ({
  type: actionTypes.SET_DASHBOARD_LAYOUTS,
  layouts,
});

export const fetchDashboard = (retailerType, dashboardSubtype) => async (dispatch, getState) => {
  try {
    const { session } = getState();
    const userId = session.id;

    dispatch(fetchDashboardRequest());

    const dashboardData = await api.fetchDashboard(userId);
    const dashboard = cloneDeep(find(dashboardData, { type: retailerType }));
    const current = find(dashboard.subtypes, { name: dashboardSubtype });

    dispatch(fetchDashboardSuccess(current, dashboard));
    const layouts = DepsService.config.layoutsConfig;
    dispatch(setDashboardLayouts(layouts));
  } catch (error) {
    console.error('FETCH_DASHBOARD', error);
    dispatch(fetchDashboardFailure());
  }
};

export const restoreDefaults = (retailerType, dashboardSubtype) => async (dispatch, getState) => {
  try {
    const { dashboard: { toSave } } = getState();
    dispatch(fetchDashboardRequest());

    const dashboard = await api.fetchDefaultDashboard(retailerType);
    const current = find(dashboard.subtypes, { name: dashboardSubtype });
    dashboard.id = toSave.id;
    dashboard.userId = toSave.userId;
    dashboard.retailerId = toSave.retailerId;

    dispatch(fetchDashboardSuccess(current, dashboard));
  } catch (error) {
    console.error('FETCH_DEFAULT_DASHBOARD', error);
    dispatch(fetchDashboardFailure());
  }
};

export const setDashboardEditMode = (isEditMode) => ({
  type: actionTypes.SET_DASHBOARD_EDIT_MODE,
  isEditMode,
});

export const setDashboardCopy = (copy) => ({
  type: actionTypes.SET_DASHBOARD_COPY,
  copy,
});

export const setDashboardCurrent = (current) => ({
  type: actionTypes.SET_DASHBOARD_CURRENT,
  isLoading: false,
  current,
});

export const setEditMode = (isEditMode, isSave) => (dispatch, getState) => {
  const { dashboard: { current, copy } } = getState();
  dispatch(setDashboardEditMode(isEditMode));

  if (isEditMode) {
    dispatch(setDashboardCopy(cloneDeep(current)));
  } else if (isSave) {
    dispatch(setDashboardCurrent(cloneDeep(current)));
  } else {
    dispatch(setDashboardCurrent(cloneDeep(copy)));
  }
};

export const saveDashboardRequest = () => ({
  type: actionTypes.SAVE_DASHBOARD_REQUEST,
});

export const saveDashboardSuccess = () => ({
  type: actionTypes.SAVE_DASHBOARD_SUCCESS,
});

export const saveDashboardFailure = () => ({
  type: actionTypes.SAVE_DASHBOARD_FAILURE,
});

export const saveDashboard = (dashboardData) => async (dispatch, getState) => {
  try {
    if (!dashboardData) {
      return;
    }
    const { dashboard } = getState();
    const dashboardToSave = cloneDeep(dashboard.toSave);
    const foundSubtypeIndex = dashboardToSave.subtypes.findIndex((subtype) => subtype.name === dashboardData.name);
    dashboardToSave.subtypes[foundSubtypeIndex] = dashboardData;

    dispatch(saveDashboardRequest());
    await api.updateDashboard(dashboardToSave);
    dispatch(saveDashboardSuccess());
  } catch (error) {
    console.error('SAVE_DASHBOARD', error);
    dispatch(saveDashboardFailure());
  }
};

function getVisibleWidgets(dashboard) {
  const visibleWidgets = [];
  dashboard.rows.forEach((row) => {
    row.columns.forEach((column) => {
      column.widgets.forEach((widget) => {
        if (!find(() => widget.type, visibleWidgets)) {
          visibleWidgets.push(widget.type);
        }
      });
    });
  });
  return visibleWidgets;
}

function getAvailableWidgets(retailer, dashboard, visibleWidgets) {
  const availableWidgets = DepsService.config.availableWidgets[retailer][dashboard.name];
  return availableWidgets.filter((widget) => !visibleWidgets.find((item) => item === widget.type));
}

function getAuthorizedWidgets(availableWidgets, session) {
  return availableWidgets.filter(
    (widget) => DepsService.config.widgets.find(
      (item) => DepsService.checkAccess({ session }, item.permission) && widget.type === item.type,
    ),
  );
}

export const setDashboardWidgetsList = (widgets) => ({
  type: actionTypes.SET_DASHBOARD_WIDGETS_LIST,
  widgets,
});

export const getWidgetsList = (retailerType) => (dispatch, getState) => {
  const { session, dashboard } = getState();
  const currentDashboard = dashboard.current;
  const newDashboard = cloneDeep(currentDashboard);
  const retailer = retailerType.toUpperCase() || session.retailer.retailerType.toUpperCase();

  const visibleWidgets = getVisibleWidgets(newDashboard);
  const availableWidgets = getAvailableWidgets(retailer, newDashboard, visibleWidgets);
  const widgets = getAuthorizedWidgets(availableWidgets, session);

  dispatch(setDashboardWidgetsList(widgets));
};

export const addWidget = ({ type }) => (dispatch, getState) => {
  const { dashboard } = getState();
  const currentDashboard = dashboard.current;
  const newDashboard = cloneDeep(currentDashboard);
  const { widgets } = dashboard;

  newDashboard.rows[0].columns[0].widgets.unshift({
    name: type,
    type,
    isEditable: true,
    isRemovable: true,
  });
  const newWidgets = widgets.filter((widgetItem) => widgetItem.type !== type);
  dispatch(setDashboardCurrent(newDashboard));
  dispatch(setDashboardWidgetsList(newWidgets));
};

export const deleteWidget = ({ type }) => (dispatch, getState) => {
  const { dashboard } = getState();
  const currentDashboard = dashboard.current;
  const newDashboard = cloneDeep(currentDashboard);

  newDashboard.rows = newDashboard.rows.reduce((reduced, row) => {
    row.columns.forEach((column) => {
      column.widgets = column.widgets.filter((widget) => widget.type !== type);
    });
    reduced.push(row);
    return reduced;
  }, []);

  dispatch(setDashboardCurrent(newDashboard));
};



// WEBPACK FOOTER //
// ./src/modules/dashboard/actions/dashboard.actions.js